/* eslint-disable consistent-return */

import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { TGTMInfo } from 'types/models/product';
import { TImpression, hasBeenImpressed } from '../components/impressions';

declare global {
    interface Window {
        dataLayer?: {
            [key: number]: unknown;
            // eslint-disable-next-line camelcase
            object_observed: boolean;
            push: (...args: Array<unknown>) => void;
        };
    }
}

/**
 * @description GTM base utils
 * @module base
 * @category widgets
 * @subcategory int_gtm
 */
export const pageProperties = {
    currentCurrency: '',
    searchList: 'Search Results',
    productsCarousel: 'Products Carousel'
};

/**
 * @description main method to push changes to dataLayer
 * @param args - data object
 */
export function report(event, data, skipEcommerceReset = false): void {
    if (typeof window.dataLayer !== 'undefined') {
        if (data.ecommerce && !skipEcommerceReset) {
            window.dataLayer.push({ ecommerce: null });
        }

        window.dataLayer.push({ event, ...data });
    }
}

export function getImpressionCategory() {
    const queryString = document.documentElement.getAttribute('data-querystring');

    if (!queryString) {
        return '';
    }

    const params = new URLSearchParams(queryString);
    const cgid = params.get('cgid');

    return cgid ?? '';
}

/**
 * @description main method to push changes to dataLayer
 * @param args - data object
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function trackCart(cart, expressItems): void {
    const items: Array<TGTMInfo> = [];

    if (cart?.items) {
        cart.items.forEach(item => {
            if (item.gtmInfo) {
                items.push(item.gtmInfo);
            }
        });
    } else if (expressItems?.length) {
        expressItems.forEach(gtmInfo => {
            items.push(gtmInfo);
        });
    }

    if (typeof window.dataLayer !== 'undefined') {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push({
            cart_products: items // eslint-disable-line camelcase
        });
    }
}

/**
 * @description Gets product list value
 * @param link - product tile
 * @returns result
 */
export function getProductList(link: TRefElementInstance): string {
    let list = 'Products Listings';

    if (window.location.search.indexOf('q=') > -1) {
        list = pageProperties.searchList;
    } else {
        const category = getImpressionCategory();

        list = category || list;
    }

    const itemList = link.data('list');

    switch (itemList) {
        case 'cart':
            list = 'Shopping Cart';
            break;
        case 'minicart':
            list = 'Mini cart';
            break;
        case 'wishlist':
            list = 'Wishlist';
            break;
        case 'orderconfirmation':
            list = 'Order confirmation';
            break;
        case 'orderdetails':
            list = 'Order details';
            break;
        case 'suggestions':
            list = 'Search Suggestions';
            break;
        case 'comparison':
            list = 'Comparison Carousel';
            break;
        case 'pdcarousel':
            list = pageProperties.productsCarousel;
            break;
        case 'pdbanner':
            list = 'Page Designer Banner';
            break;
        default:
            break;
    }

    if (link.data('recommendation')) {
        list = pageProperties.productsCarousel;
    }

    return list;
}

export type TTileInfo = {
    item_id: string; // master ID
    item_variant?: string;
};

/**
 * @description Returns tile position
 * @param tile - Tile data for searching
 * @param tile.item_id - product ID
 * @param tile.item_variant - product variant
 * @param filters - other filtering options
 * @param filters.shouldBeNotImpressed - marks if hasBeenImpressed check is needed
 * @returns tile position
 */
export function getTilePosition(
    { item_id, item_variant }: TTileInfo,
    filters = { shouldBeNotImpressed: false }
): number | undefined {
    const impressions = window.gtmImpressions?.ecommerce?.impressions;

    if (!impressions) {
        return undefined;
    }

    let filteredList: Array<TImpression> = [];

    const impressionsWithSameId = impressions.filter(i => i.item_id === item_id);

    if (item_variant) {
        filteredList = impressionsWithSameId.filter(i => i.item_variant === item_variant);
    } else {
        const impressionsWithoutVariant = impressionsWithSameId.filter(i => !i.item_variant);

        if (impressionsWithoutVariant.length > 0) {
            filteredList = impressionsWithoutVariant;
        } else {
            filteredList = impressionsWithSameId;
        }
    }

    if (filters.shouldBeNotImpressed) {
        filteredList = filteredList.filter(i => !hasBeenImpressed(item_id, i.index, item_variant));
    }

    const [result] = filteredList;

    return result?.index;
}

/**
 * @description Sends to dataLayer an information about product on product link click
 * @param link - product link
 */
export function trackProductClick(link: TRefElementInstance): void {
    const list = getProductList(link);
    const gtmData = link.data<TGTMInfo>('analytics');

    if (!gtmData) {
        return;
    }

    const tileInfo = {
        ...gtmData,
        item_list_id: list,
        index: getTilePosition(gtmData)
    };

    report('select_item', {
        ecommerce: {
            item_list_id: list,
            items: [tileInfo]
        }
    });
}
